import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { tap } from 'rxjs/operators';
import {
  SystemNotification,
  SystemNotificationApiResponse,
  SystemNotificationFollowUp
} from 'src/app/core/models/system-notification-message';

const fetchAllNotificationsUrl = '/systemNotifications/fetchAll';
const fetchCurrentNotificationsUrl = '/systemNotifications/fetchCurrent';
const saveNotificationUrl = '/systemNotifications/saveNotification';
const editNotificationUrl = '/systemNotifications/editNotification';
const deleteNotificationUrl = '/systemNotifications/deleteNotification';
const saveFollowUpUrl = '/systemNotifications/saveFollowUp';
const editFollowUpUrl = '/systemNotifications/editFollowUp';
const deleteFollowUpUrl = '/systemNotifications/deleteFollowUp';
const setNotificationReadDateUrl = '/systemNotifications/setReadDate';

@Injectable({
  providedIn: 'root'
})
export class SystemNotificationService {
  private _notificationCountUpdated: BehaviorSubject<number> = new BehaviorSubject(null);
  notificationCountUpdated: Observable<number> = this._notificationCountUpdated.asObservable();

  private _notificationAdded: Subject<SystemNotification> = new Subject();
  private _notificationUpdated: Subject<SystemNotification> = new Subject();
  private _notificationDeleted: Subject<number> = new Subject();
  notificationAdded: Observable<SystemNotification> = this._notificationAdded.asObservable();
  notificationUpdated: Observable<SystemNotification> = this._notificationUpdated.asObservable();
  notificationDeleted: Observable<number> = this._notificationDeleted.asObservable();

  private _followUpAdded: Subject<SystemNotificationFollowUp> = new Subject();
  private _followUpUpdated: Subject<SystemNotificationFollowUp> = new Subject();
  private _followUpDeleted: Subject<number> = new Subject();
  followUpAdded: Observable<SystemNotificationFollowUp> = this._followUpAdded.asObservable();
  followUpUpdated: Observable<SystemNotificationFollowUp> = this._followUpUpdated.asObservable();
  followUpDeleted: Observable<number> = this._followUpDeleted.asObservable();

  constructor(private http: HttpClient) {}

  fetchAllNotifications(): Observable<SystemNotificationApiResponse> {
    return this.http.get<SystemNotificationApiResponse>(fetchAllNotificationsUrl);
  }

  fetchCurrentNotifications(): Observable<SystemNotification[]> {
    return this.http.get<SystemNotification[]>(fetchCurrentNotificationsUrl);
  }

  saveNotification(notification: SystemNotification): Observable<void> {
    return this.http.post<void>(saveNotificationUrl, notification).pipe(
      tap(() => {
        this._notificationAdded.next(notification);
      })
    );
  }

  editNotification(notification: SystemNotification): Observable<void> {
    return this.http.post<void>(editNotificationUrl, notification).pipe(
      tap(() => {
        this._notificationUpdated.next(notification);
      })
    );
  }

  deleteNotification(id: number): Observable<void> {
    return this.http
      .delete<void>(deleteNotificationUrl, {
        params: {
          id: id
        }
      })
      .pipe(
        tap(() => {
          this._notificationDeleted.next(id);
        })
      );
  }

  saveFollowUp(followUp: SystemNotificationFollowUp): Observable<void> {
    return this.http.post<void>(saveFollowUpUrl, followUp).pipe(
      tap(() => {
        this._followUpAdded.next(followUp);
      })
    );
  }

  editFollowUp(followUp: SystemNotificationFollowUp): Observable<void> {
    return this.http.post<void>(editFollowUpUrl, followUp).pipe(
      tap(() => {
        this._followUpUpdated.next(followUp);
      })
    );
  }

  deleteFollowUp(id: number): Observable<void> {
    return this.http
      .delete<void>(deleteFollowUpUrl, {
        params: {
          id: id
        }
      })
      .pipe(
        tap(() => {
          this._followUpDeleted.next(id);
        })
      );
  }

  clearMessageCount(): void {
    this.http.put<void>(setNotificationReadDateUrl, {}).subscribe(() => this.setNotificationCount(0));
  }

  setNotificationCount(value: number): void {
    this._notificationCountUpdated.next(value);
  }

  getNotificationCount(): number {
    return this._notificationCountUpdated.value;
  }
}
